import { useEffect, useState } from 'react';

// Parse the agent's page-image citations out of an answer:
//   ![BRACBANK_ANNUAL_2021 p377](/agentbro/pdf-page?doc_key=BRACBANK_ANNUAL_2021&page=377&dpi=150)
// Returns de-duped [{ doc_key, page }] in first-seen order.
function parseCitations(text) {
  const re = /\/agentbro\/pdf-page\?[^\s)"']*?doc_key=([^&\s)"']+)[^\s)"']*?page=(\d+)/g;
  const seen = new Set();
  const out = [];
  let m;
  while ((m = re.exec(text || '')) !== null) {
    const doc_key = decodeURIComponent(m[1]);
    const page = parseInt(m[2], 10);
    const key = `${doc_key}|${page}`;
    if (!seen.has(key)) { seen.add(key); out.push({ doc_key, page }); }
  }
  return out;
}

function Badge({ children }) {
  return (
    <span className="text-[11px] text-gray-300 bg-black/40 border border-white/10 rounded-md px-1.5 py-0.5">
      {children}
    </span>
  );
}

// Clickable page citations → a paged page-image viewer (Prev/Next over the whole doc).
export default function AgentBroPdfCitations({ text, apiBase }) {
  const cites = parseCitations(text);
  const [view, setView] = useState(null); // { doc_key, page }
  if (!cites.length) return null;
  return (
    <>
      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-white/10">
        {cites.map((c) => (
          <button
            key={`${c.doc_key}-${c.page}`}
            onClick={() => setView({ doc_key: c.doc_key, page: c.page })}
            title={`Open ${c.doc_key} page ${c.page}`}
            className="text-[12px] text-blue-300 bg-black/30 border border-white/10 rounded-full px-2.5 py-0.5 whitespace-nowrap hover:border-blue-400 hover:text-blue-200 transition"
          >
            📄 {c.doc_key} · p{c.page}
          </button>
        ))}
      </div>
      {view && (
        <PdfPageModal
          apiBase={apiBase}
          docKey={view.doc_key}
          initialPage={view.page}
          onClose={() => setView(null)}
        />
      )}
    </>
  );
}

function PdfPageModal({ apiBase, docKey, initialPage, onClose }) {
  const [page, setPage] = useState(initialPage);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${apiBase}/agentbro/pdf-meta?doc_key=${encodeURIComponent(docKey)}`)
      .then((r) => r.json())
      .then((d) => { if (alive && d && !d.error) setMeta(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [apiBase, docKey]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') setPage((p) => Math.max(1, p - 1));
      else if (e.key === 'ArrowRight') setPage((p) => (count ? Math.min(count, p + 1) : p + 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  const count = meta?.page_count || 0;
  const img = `${apiBase}/agentbro/pdf-page?doc_key=${encodeURIComponent(docKey)}&page=${page}&dpi=150`;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-[#161b22] border border-white/10 rounded-xl w-[min(880px,94vw)] max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
          <span className="font-semibold text-sm truncate flex-1 text-gray-100">{meta?.title || docKey}</span>
          {meta?.symbol && <Badge>{meta.symbol}</Badge>}
          {meta?.year && <Badge>{meta.year}</Badge>}
          {meta?.doc_type && <Badge>{meta.doc_type}</Badge>}
          <button onClick={onClose} className="text-gray-400 hover:text-white text-lg leading-none px-1">✕</button>
        </div>
        <div className="flex-1 overflow-auto p-3 text-center bg-black/40">
          <img src={img} alt={`${docKey} p${page}`} className="max-w-full rounded-md inline-block" />
        </div>
        <div className="flex items-center justify-center gap-4 py-2.5 border-t border-white/10 text-sm text-gray-300">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="bg-[#0d1117] border border-white/10 rounded px-3 py-1 disabled:opacity-40 hover:border-white/30"
          >
            ‹ Prev
          </button>
          <span>page {page}{count ? ` / ${count}` : ''}</span>
          <button
            disabled={count ? page >= count : false}
            onClick={() => setPage((p) => (count ? Math.min(count, p + 1) : p + 1))}
            className="bg-[#0d1117] border border-white/10 rounded px-3 py-1 disabled:opacity-40 hover:border-white/30"
          >
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}
